import type { Block } from './bloecke.js';
import { MARKER_NAMEN } from './marker.js';

/**
 * Uebersetzt einen im Browser bearbeiteten Block zurueck in Markdown -- nur fuer den
 * erlaubten Umfang (Spec §4.3): Text, fett, kursiv, Verweis, Zeilenumbruch, Absatz, Liste,
 * Ueberschrift -- dazu Inline-Code und Inline-Bild, damit ein vorhandenes nicht verloren geht
 * (S3). Alles andere verliert sein Markup, behaelt aber seinen Text.
 *
 * Tragend ist die Byte-Treue-Regel (`uebersetzeBlock`): Wer einen Block nur anklickt, darf
 * ihn nicht veraendern. Sonst landete sein Abschnitt in `bearbeiteteAbschnitte`, und der
 * Ueberarbeitungs-Agent fasste ihn nicht mehr an (Learnings 2026-09-16).
 *
 * Tabelle, Code und Zitat sind laut Plan-Entscheidung 3 nicht editierbar (`bloecke.ts`,
 * `EDITIERBAR`) und erreichen `alsMarkdown` nie -- Aufrufer duerfen dieses Modul nur fuer
 * `editierbar`-Bloecke (Ueberschrift, Absatz, Liste) verwenden. Eine Liste ist dabei nur in
 * der EINFACHEN Form editierbar (Codex-Runde 5, Auftraggeber-Entscheidung): nicht lose, und
 * jeder Punkt hoechstens ein Textblock plus hoechstens eine Unterliste am Ende, rekursiv
 * (`bloecke.ts`, `istEinfacheListe`). Eine lose Liste oder ein Punkt mit mehreren Absaetzen
 * erreicht `alsMarkdown` deshalb nicht mehr -- die Codepfade dafuer bleiben unten trotzdem als
 * Verteidigung stehen (defensive Tiefe, falls doch einmal ein solcher Block hier landet).
 */

const BLOCK_TAGS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'blockquote',
  'pre',
  'table',
]);

function tag(knoten: Element): string {
  return knoten.tagName.toLowerCase();
}

function ohneNbsp(text: string): string {
  return text.replace(/\u00a0/g, ' ');
}

/**
 * `b`/`strong` und `i`/`em` uebersetzt `inline` (unten) ohnehin gleich -- ein blosser
 * Tag-Wechsel zwischen den beiden darf die Signatur also nicht als Aenderung zaehlen
 * (Review Focus 1).
 */
const KANONISCHER_TAG: Record<string, string> = { b: 'strong', i: 'em' };

function kanonischerTag(knoten: Element): string {
  return KANONISCHER_TAG[tag(knoten)] ?? tag(knoten);
}

/** Diese Elternelemente werten ein ABSCHLIESSENDES <br> nicht als Aenderung -- Chrome haengt
 * es beim Editieren mitunter automatisch an, ohne dass der Mensch etwas getan hat. */
const BR_UNERHEBLICH_IN = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li']);

/** Attribute, die `inline` ins Markdown uebernimmt -- deshalb zaehlen sie in der Signatur. */
const SIGNATUR_ATTRIBUTE: Record<string, readonly string[]> = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'title'],
};

/** Ein <br>, hinter dem nur noch Leerraum-Textknoten folgen, ist das letzte Element. */
function istLetztesRelevantesGeschwister(knoten: Node): boolean {
  let naechster = knoten.nextSibling;
  while (naechster) {
    if (naechster.nodeType === Node.TEXT_NODE && (naechster.nodeValue ?? '').trim() === '') {
      naechster = naechster.nextSibling;
      continue;
    }
    return false;
  }
  return true;
}

/**
 * Struktur und Text als vergleichbare Zeichenkette.
 *
 * Reiner Leerraum, der als eigener Textknoten direkt zwischen zwei Blockelementen steht
 * (Kind der Wurzel oder einer Liste), ist reine DOM-Formatierung und zaehlt nicht als
 * Aenderung -- anders als Leerraum INNERHALB eines Absatzes oder zwischen Inline-Elementen,
 * der bedeutungstragend bleibt (`<em>a</em> <em>b</em>` ist etwas anderes als
 * `<em>a</em><em>b</em>`).
 */
export function signatur(wurzel: Node): string {
  let aus = '';
  const lauf = (elternKnoten: Node, istWurzelEbene: boolean) => {
    const elternTag = elternKnoten instanceof Element ? tag(elternKnoten) : null;
    const strukturEbene = istWurzelEbene || elternTag === 'ul' || elternTag === 'ol';
    for (const kind of Array.from(elternKnoten.childNodes)) {
      if (kind.nodeType === Node.TEXT_NODE) {
        const wert = ohneNbsp(kind.nodeValue ?? '');
        if (strukturEbene && wert.trim() === '') continue;
        aus += signaturText(wert);
      } else if (kind instanceof Element) {
        if (
          tag(kind) === 'br' &&
          elternTag !== null &&
          BR_UNERHEBLICH_IN.has(elternTag) &&
          istLetztesRelevantesGeschwister(kind)
        ) {
          continue;
        }
        const kanonisch = kanonischerTag(kind);
        // `title` fliesst mit in die Signatur ein wie `href`: sonst gilt eine reine
        // Titelaenderung als unveraendert und `alsMarkdown` wird nie aufgerufen (Befund M3).
        // Dasselbe gilt fuer `src`/`alt`/`title` eines Bildes (S3). Ein fehlendes Attribut
        // (`null`) und ein leeres unterscheiden sich, weil `inline` sie verschieden uebersetzt.
        const attribute = SIGNATUR_ATTRIBUTE[kanonisch] ?? [];
        const werte = attribute.map((name) => `|${JSON.stringify(kind.getAttribute(name))}`);
        aus += `<${kanonisch}${werte.join('')}>`;
        lauf(kind, false);
        aus += `</${kanonisch}>`;
      }
    }
  };
  lauf(wurzel, true);
  return aus;
}

/**
 * Text eindeutig gegen die Tag-Marken kodieren, die dieselbe Funktion oben fuer Elemente
 * schreibt (`<strong>...</strong>`): Ohne diese Kodierung ergaeben ein `strong`-Element mit
 * Text "x" und der LITERALE Text "<strong>x</strong>" dieselbe Signatur, und `uebersetzeBlock`
 * hielte eine echte Aenderung (Element durch gleichlautenden literalen Text ersetzt) faelschlich
 * fuer unveraendert (Befund M2). Ein Backslash vor jedem "<" im Text kann in dieser Form nie
 * aus einem echten Element-Marker stammen -- der schreibt "<" immer unmittelbar vor dem
 * Tag-Namen, nie mit vorangestelltem Backslash.
 */
function signaturText(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/</g, '\\<');
}

function umschliesse(innen: string, zeichen: string): string {
  if (innen.trim() === '') return innen;
  // Markdown verlangt die Zeichen direkt am Wort; Leerraum wandert nach aussen.
  const vorn = /^\s*/.exec(innen)?.[0] ?? '';
  const hinten = /\s*$/.exec(innen)?.[0] ?? '';
  return `${vorn}${zeichen}${innen.trim()}${zeichen}${hinten}`;
}

/** Innerhalb eines Entity-Musters ("&amp;", "&#39;", "&#x27;") bliebe ein rohes "&" beim
 * erneuten Parsen als Entity-Anfang stehen -- nur dort wird es escaped. Benannte Entities
 * duerfen auch Ziffern enthalten ("&frac12;"), und ein hexadezimales "#x"/"#X" auch
 * grossgeschrieben sein ("&#X1F;") -- die vorherige, engere Fassung liess beide Faelle roh
 * durchrutschen: nach einer Bearbeitung an anderer Stelle im Block wurde daraus beim naechsten
 * Parsen wieder eine aktive Entity statt literalem Text (Codex-Fix6). */
const ENTITY_MUSTER = /&(?=#?[A-Za-z0-9]+;)/g;

/**
 * Zeichen, die `marked` beim erneuten Parsen wieder als Markdown-Syntax lesen wuerde, obwohl
 * sie im bearbeiteten DOM nur noch literaler Text sind (Befund I2, Review-Runde 2026-09-25):
 * Der Browser dekodiert eine ausgeschriebene Escape-Sequenz ("\*", "\[") oder eine
 * HTML-Entity ("&lt;") beim Rendern zu literalem Text -- schreibt man diesen Text
 * unveraendert zurueck, wird aus dem literalen Zeichen beim naechsten `marked.parse` wieder
 * aktive Syntax (aus "*nicht kursiv*" wird `<em>`, aus "[x](y)" ein Verweis, aus "<b>" ein
 * echtes Fett, aus "~~x~~" eine GFM-Durchstreichung -- Befund M1, Codex Fix-Runde 1). Die
 * Reihenfolge ist wichtig: Erst der Backslash selbst, sonst wuerden die hier neu eingefuegten
 * Backslashes im naechsten Schritt nochmal getroffen.
 */
function escapeText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/[*_[\]`<~]/g, (zeichen) => `\\${zeichen}`)
    .replace(ENTITY_MUSTER, '\\&');
}

/**
 * Ein Code-Span wertet Markdown NICHT aus -- Backslash-Escapes bleiben darin sichtbare
 * Zeichen statt Escapes (Befund Fix-Runde 2). `escapeText` darf den Inhalt deshalb nicht
 * anfassen; stattdessen wird der Zaun (die Backtick-Folge drumherum) an den Inhalt angepasst:
 * eine Backtick-Folge, die laenger ist als die laengste im Inhalt vorkommende. Beginnt oder
 * endet der Inhalt mit einem Backtick oder Leerzeichen, kommt je ein Polster-Leerzeichen dazu
 * (CommonMark), sonst wuerde der Zaun mit dem Inhalt verschmelzen.
 *
 * Zwei Randfaelle noetigen vor dieser Regel je eine eigene Behandlung (Befund M1, Codex-Runde
 * 5): Leerer Inhalt ergaebe sonst zwei direkt aufeinanderfolgende Backticks -- kein gueltiger
 * Zaun (er braeuchte einen gleich langen Gegenzaun, den es nie gibt), sie blieben als woertlicher
 * Backtick-Muell im Text stehen. Deshalb liefert leerer Inhalt gleich einen leeren String, das
 * Element entfaellt vollstaendig. Reiner Leerraum wiederum wird von CommonMark NIE gestrippt
 * ("but doesn't consist entirely of space characters") -- ein Polster wuerde also bei jeder
 * erneuten Bearbeitung als zusaetzlicher Leerraum im Inhalt bestehen bleiben und der Codespan
 * wuechse mit jeder Runde. Deshalb kein Polster, wenn der Inhalt nur aus Leerraum besteht.
 */
function alsCodeSpan(rohtext: string): string {
  if (rohtext === '') return '';
  const laengsteFolge = Math.max(0, ...(rohtext.match(/`+/g) ?? []).map((folge) => folge.length));
  const zaun = '`'.repeat(laengsteFolge + 1);
  const brauchtPolster = rohtext.trim() !== '' && /^[` ]|[` ]$/.test(rohtext);
  return brauchtPolster ? `${zaun} ${rohtext} ${zaun}` : `${zaun}${rohtext}${zaun}`;
}

/** Zeichen, die eine nackte Linkdestination `(ziel)` zerreissen wuerden -- Leerraum beendet sie
 * vorzeitig, `(`/`)` verschieben ihr eigenes Klammer-Ende, `<`/`>` waeren als Winkelklammer-Form
 * lesbar. In dieser Form gesetzt (Befund R2, Codex Fix-Runde 3), statt das Ziel roh zu
 * schreiben. Dazu jeder Backslash (S3): In der nackten Form escaped er ein folgendes
 * Satzzeichen -- ein Ziel, das auf "\" endet, verschluckte so seine eigene schliessende
 * Klammer (CommonMark; `marked` ist dort nachsichtiger, andere Parser nicht), und "a\*b" wuerde
 * zu "a*b". In der Winkelklammer-Form wird er selbst escaped und bleibt woertlich.
 */
const LINKZIEL_BRAUCHT_KLAMMERN = /[\s()<>\\]/;

/** Innerhalb `<...>` muessen nur `<`, `>` und der Backslash selbst escaped werden (CommonMark) --
 * Klammern brauchen dort keine Behandlung, genau deshalb loest die Winkelklammer-Form das
 * Klammer-Problem der nackten Form ueberhaupt erst. Reihenfolge wichtig: erst der Backslash,
 * sonst traefe der naechste Schritt auch die gerade neu eingefuegten Backslashes noch einmal.
 */
function alsLinkZiel(href: string): string {
  if (!LINKZIEL_BRAUCHT_KLAMMERN.test(href)) return href;
  const escaped = href.replace(/\\/g, '\\\\').replace(/[<>]/g, (zeichen) => `\\${zeichen}`);
  return `<${escaped}>`;
}

/** Linkziel samt optionalem title, wie es `[...](...)` und `![...](...)` gemeinsam haben. */
function zielUndTitel(ziel: string, titel: string | null): string {
  if (!titel) return alsLinkZiel(ziel);
  // Backslash zuerst escapen, sonst traefe der Anfuehrungszeichen-Schritt auch die gerade neu
  // eingefuegten Backslashes noch einmal (Befund M3).
  const titelEscaped = titel.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `${alsLinkZiel(ziel)} "${titelEscaped}"`;
}

function inline(knoten: Node): string {
  if (knoten.nodeType === Node.TEXT_NODE) return escapeText(ohneNbsp(knoten.nodeValue ?? ''));
  if (!(knoten instanceof Element)) return '';
  const innen = Array.from(knoten.childNodes).map(inline).join('');
  switch (tag(knoten)) {
    case 'strong':
    case 'b':
      return umschliesse(innen, '**');
    case 'em':
    case 'i':
      return umschliesse(innen, '*');
    case 'code':
      // Nicht `innen` (das waere bereits escaped) -- der rohe, dekodierte Text des Codespans.
      return alsCodeSpan(ohneNbsp(knoten.textContent ?? ''));
    case 'a': {
      // S3: Nur ein FEHLENDES href (z. B. von der Allowlist entfernt) ergibt Klartext. Ein
      // leeres `href=""` ist ein echter Verweis und bleibt einer: `[T]()`.
      const href = knoten.getAttribute('href');
      if (href === null) return innen;
      return `[${innen}](${zielUndTitel(href, knoten.getAttribute('title'))})`;
    }
    case 'img': {
      // S3: Ein Inline-Bild ging bisher ganz verloren (kein Text, `innen` leer). Dieselben
      // Zielregeln wie beim Verweis; `alt` ist Klartext in eckigen Klammern und wird wie Text
      // escaped. Fehlt `src` (von der Allowlist entfernt), bleibt ein leeres Ziel -- dasselbe
      // Element, das der Mensch vor sich hatte.
      const alt = escapeText(ohneNbsp(knoten.getAttribute('alt') ?? ''));
      const src = knoten.getAttribute('src') ?? '';
      return `![${alt}](${zielUndTitel(src, knoten.getAttribute('title'))})`;
    }
    case 'br':
      return '<br>';
    default:
      return innen;
  }
}

function einzeilig(text: string): string {
  return text.replace(/\s*\n\s*/g, ' ').trim();
}

/** Eine Zeile aus drei oder mehr gleichen Zeichen (mit optionalem Leerraum dazwischen) waere
 * als Trennlinie (<hr>) lesbar -- Stern und Unterstrich escaped `escapeText` (oben) bereits
 * einzeln, hier bleibt vor allem der Bindestrich-Fall uebrig ("---"). */
const TRENNLINIE_MUSTER = /^(\s*)([-*_])(\s*\2){2,}\s*$/;
/** Eine Zeile, die mit drei Backticks oder Tilden beginnt, oeffnet einen Code-Zaun --
 * Backticks escaped `escapeText` bereits einzeln, die Tilde seit Befund M1 (Codex
 * Fix-Runde 1) ebenfalls, dieses Muster bleibt trotzdem als zweite Absicherung stehen. */
const CODE_ZAUN_MUSTER = /^\s*(`{3,}|~{3,})/;
/** Eine Absatzzeile aus NUR "-" oder NUR "=" macht die Zeile DAVOR zu einer Setext-Ueberschrift
 * (H2 bzw. H1) -- das gilt fuer JEDE Anzahl ab EINEM Zeichen (Befund R1, Codex Fix-Runde 3),
 * anders als bei der Trennlinie (`TRENNLINIE_MUSTER` oben), die erst ab drei Zeichen greift.
 * Ersetzt die fruehere, auf "=" beschraenkte Fassung (Befund M1, Codex Fix-Runde 1): der
 * Bindestrich-Fall war bislang nur ab drei Bindestrichen abgedeckt (durch die Ueberschneidung
 * mit `TRENNLINIE_MUSTER`), ein einzelner oder doppelter Bindestrich blieb unentschaerft,
 * obwohl schon ein einziger Bindestrich als Setext-Unterstreichung genuegt.
 */
const SETEXT_MUSTER = /^\s*[-=]+\s*$/;
/** Eine Zeile, die wie eine GFM-Tabellen-Trennzeile aussieht, macht die Zeile DAVOR zum
 * Tabellenkopf (Befund R1, Codex Fix-Runde 3) -- Zellen aus ":?-+:?", durch "|" getrennt,
 * mindestens ein "|" (sonst waere die Zeile nur eine gewoehnliche Bindestrich-Zeile, die
 * `SETEXT_MUSTER` bereits abdeckt). Escapt wird nur das ERSTE "-" bzw. ":" -- das reicht, um
 * `marked` die Zeile als Trennzeile verwehren, und bleibt dabei minimal-invasiv.
 */
const TABELLE_TRENNZEILE_MUSTER = /^\s*\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)+\|?\s*$/;

/** Ein Absatz, der mit `#`, `- ` oder `1. ` beginnt, wuerde sonst zu Ueberschrift oder Liste. */
function entschaerfe(zeile: string): string {
  if (/^\s*\d+[.)]\s/.test(zeile)) return zeile.replace(/^(\s*\d+)([.)])/, '$1\\$2');
  if (TABELLE_TRENNZEILE_MUSTER.test(zeile)) return zeile.replace(/[-:]/, '\\$&');
  if (TRENNLINIE_MUSTER.test(zeile) || CODE_ZAUN_MUSTER.test(zeile) || SETEXT_MUSTER.test(zeile)) {
    return zeile.replace(/^(\s*)/, '$1\\');
  }
  if (/^\s*(#{1,6}\s|[-*+]\s|>)/.test(zeile)) return zeile.replace(/^(\s*)/, '$1\\');
  return zeile;
}

/**
 * `<br>` am Ende steht nur fuer die leere Zeile, die Chrome in ein geleertes Feld setzt --
 * der Lookbehind verhindert, dass ein ESCAPTES literales "\<br>" (Text, kein Element,
 * `escapeText` hat das "<" escaped) mitsamt seinem Backslash verschwindet (Fix-Runde 2).
 */
const ABSCHLIESSENDES_BR = /(?<!\\)(<br>\s*)+$/;

function absatz(text: string): string {
  const bereinigt = text.replace(ABSCHLIESSENDES_BR, '').trim();
  return bereinigt.split('\n').map(entschaerfe).join('\n');
}

interface ListenStil {
  geordnet: boolean;
  zeichen: string;
  start: number;
  trenner: string;
  einzug: string;
}

function listenStilAus(raw: string): ListenStil {
  const t = /^(\s*)(?:([-*+])|(\d+)([.)]))\s/.exec(raw);
  if (!t) return { geordnet: false, zeichen: '-', start: 1, trenner: '.', einzug: '' };
  if (t[2]) return { geordnet: false, zeichen: t[2], start: 1, trenner: '.', einzug: t[1] ?? '' };
  return {
    geordnet: true,
    zeichen: '',
    start: Number(t[3]),
    trenner: t[4] ?? '.',
    einzug: t[1] ?? '',
  };
}

/**
 * Zaehlt je Einzugstiefe (Schluessel = Zeichenbreite des Eltern-Einzugs), wie viele Laeufe an
 * dieser Tiefe in diesem Aufruf von `alsMarkdown` schon einer Unterliste zugeteilt wurden --
 * gemeinsam ueber die ganze Rekursion eines Blocks (Befund M2).
 */
type Laufzaehler = Map<number, number>;

/**
 * Stil einer Unterliste: aus dem k-TEN zusammenhaengenden Lauf tiefer eingerueckter
 * Markerzeilen im Original-`raw` gelesen (Befund M2), nicht mehr immer aus dem ersten -- sonst
 * bekaeme jede weitere Unterliste an derselben Einzugstiefe den Stil der allerersten
 * aufgezwungen. Ein Lauf beginnt an einer Markerzeile, die tiefer eingerueckt ist als
 * `elternStil.einzug`, und endet an der naechsten Markerzeile, die das nicht mehr ist;
 * Zeilen ohne eigenen Marker (Fortsetzungstext) unterbrechen ihn nicht. `laeufe` merkt sich
 * pro Tiefe, wie viele Laeufe schon vergeben sind, und wird bei jedem Treffer weitergezaehlt.
 * Ohne (weiteren) Treffer -- z. B. eine neu eingefuegte Unterliste ohne Vorbild im Original --
 * gilt als Rueckfall der Einzug der Elternliste plus die Breite ihres Markers plus das
 * Leerzeichen danach.
 */
function unterlistenStil(
  elternStil: ListenStil,
  blockRaw: string,
  laeufe: Laufzaehler,
): ListenStil {
  const mindestEinzug = elternStil.einzug.length;
  const zuUeberspringen = laeufe.get(mindestEinzug) ?? 0;
  let laufIndex = -1;
  let inLauf = false;
  for (const zeile of blockRaw.split('\n')) {
    const t = /^(\s*)(?:([-*+])|(\d+)([.)]))\s/.exec(zeile);
    if (!t) continue;
    const einzug = t[1] ?? '';
    if (einzug.length <= mindestEinzug) {
      inLauf = false;
      continue;
    }
    if (inLauf) continue;
    inLauf = true;
    laufIndex += 1;
    if (laufIndex !== zuUeberspringen) continue;
    laeufe.set(mindestEinzug, zuUeberspringen + 1);
    if (t[2]) return { geordnet: false, zeichen: t[2], start: 1, trenner: '.', einzug };
    return { geordnet: true, zeichen: '', start: Number(t[3]), trenner: t[4] ?? '.', einzug };
  }
  const markerBreite = elternStil.geordnet
    ? `${elternStil.start}${elternStil.trenner}`.length
    : elternStil.zeichen.length;
  return {
    geordnet: false,
    zeichen: '-',
    start: 1,
    trenner: '.',
    einzug: `${elternStil.einzug}${' '.repeat(markerBreite + 1)}`,
  };
}

function liste(element: Element, stil: ListenStil, blockRaw: string, laeufe: Laufzaehler): string {
  const geordnet = tag(element) === 'ol';
  const punkte = Array.from(element.children).filter((k) => tag(k) === 'li');
  // `marked` wrappt entweder ALLE Punkte einer Liste in <p> (lockere Liste -- Leerzeile im
  // Original) oder KEINEN (enge Liste) -- ein Blick auf einen Punkt genuegt fuer die ganze
  // Liste (Befund H1). Eine lockere Liste bleibt durch Leerzeilen zwischen den Punkten
  // getrennt, eine enge Liste bleibt eng.
  const listeLocker = punkte.some((li) =>
    Array.from(li.children).some((k) => tag(k) === 'p' || tag(k) === 'div'),
  );
  const punktZeilen: string[] = [];
  let zaehler = 0;
  punkte.forEach((li) => {
    // Unterlisten (verschachteltes ul/ol) getrennt behandeln, sonst verschluckt `inline` sie
    // ohne Marker und ohne Zeilenumbruch (Befund I3).
    const unterlisten: Element[] = Array.from(li.children).filter(
      (k) => tag(k) === 'ul' || tag(k) === 'ol',
    );
    const unterlistenSet = new Set<ChildNode>(unterlisten);
    // Blockkinder (p/div) eines lockeren Punkts sind eigene Absaetze -- eine Behandlung, die
    // sie ueber `einzeilig` zu einer Zeile zusammenklebt, verlor bislang die Absatzstruktur
    // (Befund H1). `teileAus` uebersetzt jedes Blockkind wie einen eigenstaendigen Absatz,
    // samt `entschaerfe`.
    const blockKinder = Array.from(li.children).filter(
      (k) => !unterlistenSet.has(k) && (tag(k) === 'p' || tag(k) === 'div'),
    );
    const lockererPunkt = blockKinder.length > 0;
    const absaetze = lockererPunkt
      ? blockKinder.flatMap((kind) => teileAus(kind, stil, blockRaw, laeufe))
      : [];
    const eigeneKnoten = Array.from(li.childNodes).filter((k) => !unterlistenSet.has(k));
    // Ein Listenpunkt, dessen eigener Text mit `#`, `- ` oder `1. ` beginnt, braucht dieselbe
    // Entschaerfung wie ein Absatz (Befund I4) -- sonst entsteht eine verschachtelte Liste
    // oder eine Ueberschrift.
    const rohText = lockererPunkt
      ? ''
      : entschaerfe(einzeilig(eigeneKnoten.map(inline).join('')).replace(ABSCHLIESSENDES_BR, ''));
    // Ein geleerter Listenpunkt (Enter am Ende, dann nichts getippt) faellt weg -- aber nur,
    // wenn er auch keine Unterliste traegt.
    const istLeer = lockererPunkt ? absaetze.length === 0 : rohText === '';
    if (istLeer && unterlisten.length === 0) return;
    const nummer = (stil.geordnet ? stil.start : 1) + zaehler;
    zaehler += 1;
    const marker = geordnet
      ? `${nummer}${stil.geordnet ? stil.trenner : '.'}`
      : stil.geordnet
        ? '-'
        : stil.zeichen;

    const eigeneZeilen: string[] = [];
    if (lockererPunkt) {
      // Fortsetzungseinzug: Einzug des Punkts plus Markerbreite plus 1 (das Leerzeichen
      // nach dem Marker) -- jede Zeile ausser der allerersten ruckt darauf ein, jeder
      // weitere Absatz wird durch eine Leerzeile abgesetzt.
      const fortsetzungsEinzug = `${stil.einzug}${' '.repeat(marker.length + 1)}`;
      absaetze.forEach((absatzText, i) => {
        if (i > 0) eigeneZeilen.push('');
        absatzText.split('\n').forEach((zeile, j) => {
          eigeneZeilen.push(
            i === 0 && j === 0
              ? `${stil.einzug}${marker} ${zeile}`
              : `${fortsetzungsEinzug}${zeile}`,
          );
        });
      });
    } else if (rohText !== '') {
      eigeneZeilen.push(`${stil.einzug}${marker} ${rohText}`);
    }

    const unterlistenZeilen: string[] = [];
    unterlisten.forEach((unter) => {
      const unterText = liste(unter, unterlistenStil(stil, blockRaw, laeufe), blockRaw, laeufe);
      if (unterText !== '') unterlistenZeilen.push(unterText);
    });

    punktZeilen.push([...eigeneZeilen, ...unterlistenZeilen].join('\n'));
  });
  return punktZeilen.join(listeLocker ? '\n\n' : '\n');
}

function teileAus(
  wurzel: Element,
  stil: ListenStil,
  blockRaw: string,
  laeufe: Laufzaehler,
): string[] {
  const teile: string[] = [];
  let laufend = '';
  const abschliessen = () => {
    const text = absatz(laufend);
    if (text !== '') teile.push(text);
    laufend = '';
  };
  for (const kind of Array.from(wurzel.childNodes)) {
    if (!(kind instanceof Element) || !BLOCK_TAGS.has(tag(kind))) {
      laufend += inline(kind);
      continue;
    }
    abschliessen();
    const t = tag(kind);
    if (/^h[1-6]$/.test(t)) {
      const text = einzeilig(inline(kind)).replace(ABSCHLIESSENDES_BR, '');
      if (text !== '') teile.push(`${'#'.repeat(Number(t[1]))} ${text}`);
    } else if (t === 'ul' || t === 'ol') {
      const text = liste(kind, stil, blockRaw, laeufe);
      if (text !== '') teile.push(text);
    } else {
      teile.push(...teileAus(kind, stil, blockRaw, laeufe));
    }
  }
  abschliessen();
  return teile;
}

/**
 * Inline-Marker einer Produktseite (Spec 2026-10-12 §5.5) gehen OHNE `\[`/`\]` zurueck: n8n
 * entfernt Pruefhinweise vor 7 per Regex, ein escapter Marker hinterliesse dort einen Backslash
 * im Word-Dokument, und der Content-Experte soll die Marker so sehen, wie sie geschrieben wurden.
 *
 * Gearbeitet wird auf dem fertigen Block-Markdown, nicht je Textknoten: Als Chip steht der Marker
 * allein im Textknoten seines Spans, das Zeichen danach erst im naechsten Geschwister. Freigelegt
 * wird nur ein Marker, dem weder "(" noch "[" noch ":" folgt -- sonst laese `marked` ihn als
 * Verweis bzw. Verweisdefinition (gemessen 2026-10-12: "[Platzhalter: x]: y" verschwindet ganz).
 *
 * Das Muster trifft nur `\[` aus `escapeText`: Dort wird jeder literale Backslash verdoppelt, ein
 * `\[` ist also immer eine escapte Klammer. Im Marker ist ein Escape-Paar erlaubt, ausser `\]`.
 * Bekannte Grenze: Ein Codespan, in dem woertlich `\[Platzhalter: ...\]` steht, wird mit
 * freigelegt. Codespans laesst `escapeText` roh, dieses Muster unterscheidet sie nicht.
 */
const ESCAPTER_MARKER = new RegExp(
  String.raw`\\\[((?:${MARKER_NAMEN}):(?:[^\\\]\n]|\\[^\]\n])*)\\\](?![(\[:])`,
  'g',
);

function markerFreilegen(markdown: string): string {
  return markdown.replace(ESCAPTER_MARKER, '[$1]');
}

export function alsMarkdown(wurzel: Element, block: Pick<Block, 'raw'>): string {
  const laeufe: Laufzaehler = new Map();
  const koerper = markerFreilegen(
    teileAus(wurzel, listenStilAus(block.raw), block.raw, laeufe).join('\n\n'),
  );
  if (koerper === '') return '';
  // Leerraum um den Block (z. B. das "\n" hinter "## Titel") gehoert zum Original -- aber nur
  // bis EINSCHLIESSLICH des letzten Zeilenumbruchs: Die Einrueckung der ersten Zeile ist bei
  // einer Liste bereits Blockinhalt, den `liste()` selbst ueber `stil.einzug` erzeugt. Ohne
  // diese Kappung wuerde sie hier ein zweites Mal vorangestellt (Befund M1). Ueberschrift und
  // Absatz verlieren dadurch nichts Sichtbares: ein bis drei fuehrende Leerzeichen sind in
  // CommonMark ohne Bedeutung.
  const vornRoh = /^\s*/.exec(block.raw)?.[0] ?? '';
  const vorn = vornRoh.slice(0, vornRoh.lastIndexOf('\n') + 1);
  const hinten = /\s*$/.exec(block.raw)?.[0] ?? '';
  return vorn + koerper + hinten;
}

export function uebersetzeBlock(
  wurzel: Element,
  block: Pick<Block, 'raw'>,
  signaturBeimOeffnen: string,
): string {
  if (signatur(wurzel) === signaturBeimOeffnen) return block.raw;
  return alsMarkdown(wurzel, block);
}
